import { useEffect, useState } from 'react';

import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Slider } from '@repo/videa-components/src/index';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { useUpdateRBLSettings } from '../../../../../react-query/useUpdateRBLSettings';
import type { radiographicBoneLevelSetting } from '../../../../../utils/rbl';
import {
  radiographicBoneLevelValues,
  useRadiographicBoneLevelLabels,
} from '../../../../../utils/rbl';
import { updatePracticeRBLThreshold } from '../../../../login/LoginActions';

import { RBLColorIndicator } from './RBLColorIndicator';
import { RBLColorSettingsTableRow } from './RBLColorSettingsTableRow';

interface RBLColorSettingsProps {
  isEmbedded?: boolean;
}

export const RBLColorSettings = (props: RBLColorSettingsProps) => {
  // selectors
  const practice = useSelector(
    (state: RootStateOrAny) => state.login.user.practice,
  );
  const user = useSelector((state: any) => state.login.user);

  // hooks
  const { isEmbedded } = props;
  const { t } = useLingui();
  const translatedLabels = useRadiographicBoneLevelLabels();
  const { search } = useLocation();
  const history = useHistory();

  const defaultPracticeRBLSetting = practice?.rblSettings?.templateName;

  // state
  const [isRBLColorSettingsOpen, setIsRBLColorSettingsOpen] = useState(false);
  const [thresholdSetting, setThresholdSetting] = useState<
    keyof typeof radiographicBoneLevelSetting
  >(defaultPracticeRBLSetting || 'default');

  const dispatch = useDispatch();
  const { mutate } = useUpdateRBLSettings();

  // handlers
  const handleSliderChange = (value: number[]) => {
    const numericValue = value[0];
    const newThresholdSetting = Object.keys(radiographicBoneLevelValues)[
      numericValue
    ];
    setThresholdSetting(
      newThresholdSetting as keyof typeof radiographicBoneLevelValues,
    );
  };

  const handleConfirm = () => {
    dispatch(updatePracticeRBLThreshold(thresholdSetting));

    if (user.primaryPracticeId && user.primaryOrganizationId) {
      mutate({ templateName: thresholdSetting });
    }
    setIsRBLColorSettingsOpen(false);
  };

  const handleReset = () => {
    dispatch(updatePracticeRBLThreshold('default'));
    mutate({ templateName: 'default' });
    setThresholdSetting('default');
  };

  const stepIndicators = Object.entries(translatedLabels).map(
    ([key, setting]) => ({
      value:
        radiographicBoneLevelValues[
          key as keyof typeof radiographicBoneLevelValues
        ].value,
      label: setting.label,
    }),
  );

  const rblSettingsContent = (
    <>
      <div className="vai-flex vai-justify-center vai-w-3/4 vai-mx-auto vai-flex-col vai-gap-2">
        <Slider
          data-testid="rbl-threshold-slider"
          // Gives the slider track a blue color
          className="[&_.vai-bg-secondary]:vai-bg-blue-200 [&_.vai-bg-primary]:vai-bg-blue-500 [&_.vai-border-primary]:vai-border-blue-500"
          value={[
            radiographicBoneLevelValues[thresholdSetting || 'default'].value,
          ]}
          onValueChange={handleSliderChange}
          max={3}
          min={0}
          step={1}
        />
        <div className="vai-flex vai-justify-between vai-text-xs vai-text-gray-600 vai-px-1">
          {stepIndicators.map((mark) => (
            <span key={mark.value} className="vai-text-center">
              {mark.label}
            </span>
          ))}
        </div>
      </div>

      <div className="vai-w-full vai-p-6 vai-mt-4">
        <div className="vai-w-full">
          {/* Header row with colors */}
          <div className="vai-flex vai-w-full vai-border-b vai-border-gray-200 vai-pb-4 vai-mb-4 vai-justify-between">
            <div className="vai-w-1/5" />
            <RBLColorIndicator color="green" label={<Trans>Green</Trans>} />
            <RBLColorIndicator color="yellow" label={<Trans>Yellow</Trans>} />
            <RBLColorIndicator color="orange" label={<Trans>Orange</Trans>} />
            <RBLColorIndicator color="red" label={<Trans>Red</Trans>} />
          </div>

          {/* Data rows */}
          <RBLColorSettingsTableRow
            label={<Trans>BWs</Trans>}
            values={translatedLabels[thresholdSetting].bitewings}
            dataCyPrefix="bw"
          />
          <RBLColorSettingsTableRow
            label={<Trans>PAs</Trans>}
            values={translatedLabels[thresholdSetting].periapical}
            dataCyPrefix="pa"
          />
        </div>
      </div>
      <span className="vai-font-bold">
        <Trans>
          NOTE: This change will affect all users across your practice. You or
          another user can adjust it at any time. Confer with your providers to
          decide which settings changes to make. These settings are available
          from within the viewer as well.
        </Trans>
      </span>
      <DialogFooter>
        <Button
          variant="ghost"
          onClick={() => setIsRBLColorSettingsOpen(false)}
        >
          <Trans>CANCEL</Trans>
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleConfirm}
              data-testid="confirm-changes-button"
            >
              <Trans>CONFIRM CHANGES</Trans>
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="center"
            portal
            className="vai-max-w-xs"
          >
            <p>{t`This setting should be set by the lead doctor(s) at your office - please communicate with them as the team is getting started with VideaAI.`}</p>
          </TooltipContent>
        </Tooltip>
      </DialogFooter>
    </>
  );

  // Will run when the RBL measurement link to RBL color settings is clicked
  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const shouldOpenRBLColorSettings = !!queryParams.get(
      'rbl-color-settings-open',
    );
    if (!shouldOpenRBLColorSettings) {
      return;
    }
    setIsRBLColorSettingsOpen(true);
    queryParams.delete('rbl-color-settings-open');
    history.replace({ search: queryParams.toString() });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'history'. Either include it or remove the dependency array. */
  }, [search]);

  if (isEmbedded) {
    return rblSettingsContent;
  }

  return (
    <>
      <>
        <Button
          onClick={() => setIsRBLColorSettingsOpen(true)}
          variant="outline"
          className="hover:vai-bg-blue-200 vai-border-blue-300 vai-text-blue-800"
          data-testid="rbl-color-settings-button"
        >
          <FontAwesomeIcon icon={faListCheck} />
          <Trans>RBL Color Setting</Trans>
        </Button>
        {!!defaultPracticeRBLSetting &&
          defaultPracticeRBLSetting !== 'default' && (
            <Button
              variant="outline"
              onClick={handleReset}
              data-testid="reset-to-default-button"
            >
              <Trans>RESET TO DEFAULT</Trans>
            </Button>
          )}
      </>
      <Dialog
        open={isRBLColorSettingsOpen}
        onOpenChange={setIsRBLColorSettingsOpen}
      >
        <DialogContent
          data-testid="rbl-settings-dialog"
          className="vai-max-w-[800px] vai-w-[90vw] vai-max-h-[95vh] vai-overflow-y-auto vai-gap-6 vai-p-6"
        >
          <DialogHeader>
            <DialogTitle className="vai-text-xl vai-font-semibold">
              <Trans>Radiographic Bone Level Measurement Settings</Trans>
            </DialogTitle>
          </DialogHeader>

          <div className="vai-space-y-4">
            <div className="vai-text-sm vai-text-muted-foreground vai-font-medium">
              <Trans>
                RBL Measurements For the Selected Visualization Setting
              </Trans>
            </div>
            <div className="vai-text-sm vai-text-muted-foreground">
              <Trans>
                Videa Perio Assist is a medical device focused on measurement of
                radiographic bone level (RBL). To adjust the thresholds for the
                colors assigned to the measurements, adjust the slider below.
              </Trans>
            </div>
            {rblSettingsContent}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
